import {
  Button,
  Checkbox,
  Container,
  Flex,
  IconButton,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { useState } from "react";

import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useIsMobile } from "@equidefi/portals/hooks/useIsMobile";
import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";
import { useChangePassword } from "@equidefi/portals/hooks/useUsers";
import { CardList, CardListItem, Heading, Password, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { ProfileModal } from "@/pages/app/vault/profile/ProfileModal";
import { forgotPasswordSchema } from "@/pages/login/reset";

const initialValues = {
  password: "",
  confirmPassword: "",
};

const Profile = (props) => {
  useDocumentTitle(["Account"]);

  const profileModal = useDisclosure();
  const toast = useToast();
  const { data: me, refetch } = useCurrentUser();
  const isMobile = useIsMobile();

  const changePassword = useChangePassword();
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async ({ password }, { resetForm }) => {
    try {
      await changePassword.mutateAsync({ password });
      resetForm();
      toast({
        status: "success",
        description: "You successfully changed your password.",
      });
    } catch (e) {
      console.error(e);
      toast({
        status: "error",
        description: e.data.error,
      });
    }
  };

  const { errors, touched, getFieldProps, handleSubmit, isValid } = useFormik({
    initialValues: initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit,
    validateOnMount: true,
    validateOnChange: true,
  });

  return (
    <>
      <Container minW="full" my="4">
        {!isMobile && (
          <Heading
            textStyle="h1"
            as="h1"
            my="10"
            fontWeight="bold"
            fontSize="30px">
            Account
          </Heading>
        )}
        <Flex align="center">
          <CardList w="full">
            <CardListItem
              action={
                <IconButton
                  onClick={profileModal.onOpen}
                  icon={<Icon.Edit size="1.5em" />}
                  color="equidefi.blue"
                  alignItems="flex-start"
                  pt="4"
                />
              }>
              <Heading mb="1" fontWeight="bold">
                Profile
              </Heading>
              <Text m="0">
                {me?.first_name && me?.last_name
                  ? `${me?.first_name} ${me?.middle_name ?? ""} ${
                      me?.last_name
                    }`
                  : "Full Name"}
              </Text>
              <Text m="0">{me?.email}</Text>
              <Text m="0">{me?.phone}</Text>
            </CardListItem>
            <CardListItem>
              <Heading mb="2" fontWeight="bold">
                Update Password
              </Heading>
              <form noValidate onSubmit={handleSubmit}>
                <Text mb="5" textStyle="body2">
                  Enter a secure password for your account
                </Text>
                <Password
                  placeholder="Password"
                  error={errors.password}
                  isInvalid={errors.password && touched.password}
                  h="12"
                  {...getFieldProps("password")}
                  showPasswordOverwrite={showPassword}
                />

                <Password
                  placeholder="Confirm Password"
                  error={errors.confirmPassword}
                  isInvalid={errors.confirmPassword && touched.confirmPassword}
                  h="12"
                  {...getFieldProps("confirmPassword")}
                  showPasswordOverwrite={showPassword}
                />

                <Checkbox
                  mb="6"
                  checked={showPassword}
                  onChange={() => setShowPassword((prev) => !prev)}>
                  Show password
                </Checkbox>

                <Button
                  isLoading={changePassword.isLoading}
                  isDisabled={!isValid}
                  type="submit"
                  h="12"
                  w="full">
                  Reset Password
                </Button>
              </form>
            </CardListItem>
          </CardList>
        </Flex>
      </Container>
      <ProfileModal modal={profileModal} onSave={refetch} me={me} />
    </>
  );
};

export default Profile;
